import { LoadingOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// RETRY/SKIPPED/EXPIRE 是后端 AwsFileMergeStatusEnum 的真实状态:
// RETRY=合并失败等待重试(视同处理中);SKIPPED=音频不足1分钟后端跳过多模态(终态);EXPIRE=不适用多模态的房间(终态)
export type IMeetStatus = 'FINISH' | 'INIT' | 'WAIT_DO' | 'UNKNOWN' | 'ERROR' | 'FAILED' | 'MERGING' | 'RETRY' | 'SKIPPED' | 'EXPIRE' | 'NO_ACCESS';

interface MeetStatusProps {
  meetStatus: IMeetStatus;
  handleEndMeeting?: () => void;
  contentType?: 'summary' | 'transcription';
  /** 紧凑状态条形态:平铺在会中转录内容上方,而不是占满内容区居中展示 */
  compact?: boolean;
}

const MeetStatus: React.FC<MeetStatusProps> = ({ meetStatus, handleEndMeeting, contentType, compact }) => {
  const { t } = useTranslation();
  const [, setCountdown] = useState(30);

  useEffect(() => {
    if (meetStatus === 'WAIT_DO' || meetStatus === 'MERGING' || meetStatus === 'RETRY') {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            window.location.reload();
            return 30;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [meetStatus]);

  const handleEndMeetingClick = () => {
    Modal.confirm({
      title: t('meeting.confirmEndMeeting'),
      icon: null,
      content: t('meeting.endMeetingWarning'),
      okText: t('meeting.endMeeting'),
      cancelText: t('meeting.cancel'),
      onOk() {
        handleEndMeeting?.();
      },
    });
  };

  const renderContent = () => {
    switch (meetStatus) {
      // FAILED 是"未生成转录"的终态：下方仍会渲染完整实时字幕，
      // 用与 EXPIRE 一致的中性文案，避免警告框让用户误以为记录损坏（字幕并未丢失）
      case 'FAILED':
        return <div className="loading-text">{t('meeting.transcriptionUnavailable')}</div>;
      // ERROR 是前端获取状态失败（网络/接口异常），保留警告样式提示重试
      case 'ERROR':
        return (
          <div>
            <div className="warning-title">{t('meeting.getMeetingInfoFailed')}</div>
            <div className="warning-text">{t('meeting.getSummaryFailedReason')}</div>
            <ul className="warning-list">
              <li>{t('meeting.getSummaryFailedReason1')}</li>
              <li>{t('meeting.getSummaryFailedReason2')}</li>
              <li>{t('meeting.getSummaryFailedReason3')}</li>
            </ul>
            <span className="warning-text" style={{ marginTop: '0px' }}>{t('meeting.getSummaryFailedReason4')}</span>
          </div>
        );
      case 'UNKNOWN':
        return (
          <div className="loading-text">
            <LoadingOutlined />
            {t('meeting.gettingMeetingInfo')}
          </div>
        );
      case 'NO_ACCESS':
        return (
          <div className="loading-text">
            {contentType === 'transcription'
              ? t('meeting.transcriptionNotAvailable')
              : t('meeting.summaryNotAvailable')}
          </div>
        );
      case 'INIT':
        return (
          <>
            <div className="loading-text" style={{ textAlign: 'left' }}>
              {t('meeting.meetingInProgress')}
              <br />
              <br />
              {t('meeting.meetingEnded')}
              <Button onClick={handleEndMeetingClick} style={{ marginLeft: '10px' }}>{t('meeting.endMeeting')}</Button>
            </div>
          </>
        );
      case 'WAIT_DO':
      case 'MERGING':
      case 'RETRY':
        return (
          <div className="loading-text">
            <LoadingOutlined />
            {t('meeting.processingMeeting')}
          </div>
        );
      case 'SKIPPED':
        // 后端对不足 1 分钟的录音跳过合并与多模态转录(省费用),实时转录不受影响
        return <div className="loading-text">{t('meeting.meetingTooShort')}</div>;
      case 'EXPIRE':
        return <div className="loading-text">{t('meeting.transcriptionUnavailable')}</div>;
      case 'FINISH':
        return <div className="loading-text">{t('meeting.meetingInfoCompleted')}</div>;
      default:
        return <div className="loading-text">{t('meeting.meetingWaiting')}</div>;
    }
  };

  return (
    <div className={compact ? 'gemini-content meet-status-banner' : 'gemini-content'}>
      <div className="loading-container">
        {renderContent()}
      </div>
    </div>
  );
};

export default MeetStatus;
